import React from "react";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, Globe } from "lucide-react";

// Flag emojis render natively on all major OSes; we keep them in <span> with
// a font-emoji fallback so they show even on Windows Chrome.
const LANGS = [
    { code: "pt", label: "Português", flag: "🇵🇹", short: "PT" },
    { code: "en", label: "English", flag: "🇬🇧", short: "EN" },
    { code: "es", label: "Español", flag: "🇪🇸", short: "ES" },
];

const flagStyle = {
    fontFamily:
        '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","EmojiOne Color","Twemoji Mozilla","Segoe UI Symbol",sans-serif',
    fontSize: "1.1rem",
    lineHeight: 1,
};

export const LanguageSwitcher = ({ compact = false }) => {
    const { i18n, t } = useTranslation();
    const current = (i18n.language || "pt").split("-")[0];
    const active = LANGS.find((l) => l.code === current) || LANGS[0];

    const change = (code) => {
        i18n.changeLanguage(code);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid="language-switcher-trigger"
                aria-label={t("language.select")}
                className={`inline-flex items-center gap-2 rounded-full border border-stone-200/80 bg-white/70 backdrop-blur hover:bg-white hover:border-stone-300 transition-colors px-3 ${
                    compact ? "h-9" : "h-10"
                }`}
            >
                <span style={flagStyle} aria-hidden="true">{active.flag}</span>
                <span className="text-xs uppercase tracking-[0.18em] text-stone-700">
                    {active.short}
                </span>
                <Globe size={14} className="text-stone-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
                {LANGS.map((l) => (
                    <DropdownMenuItem
                        key={l.code}
                        onClick={() => change(l.code)}
                        data-testid={`language-option-${l.code}`}
                        className="flex items-center gap-3 cursor-pointer"
                    >
                        <span style={flagStyle} aria-hidden="true">{l.flag}</span>
                        <span className="flex-1 text-sm text-stone-800">{l.label}</span>
                        {l.code === current && <Check size={14} className="text-gold-deep" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageSwitcher;
